import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Form,
  Card,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Upload,
  message,
  Table,
  Modal,
  InputNumber,
} from "antd";
import {
  SaveOutlined,
  SendOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useList } from "@refinedev/core";
import { PURCHASE_ORDER_QUERY } from "../../purchase-order/queries";
import { VENDOR_LIST_QUERY } from "../../vendors/queries";
import { REQUEST_TEMPLATE_LIST_QUERY } from "../../purchase-template/queries";
import {
  Receiving,
  ReceivingItem,
  CreateReceivingInput,
  UpdateReceivingInput,
  ReceivingStatus,
  VendorInfo,
} from "../interface";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";

interface ReceivingFormProps {
  form: any;
  initialValues?: Partial<Receiving>;
  isEdit?: boolean;
  onSaveDraft?: (values: CreateReceivingInput | UpdateReceivingInput) => Promise<void>;
  onSubmit?: (values: CreateReceivingInput | UpdateReceivingInput) => Promise<void>;
  purchaseOrderId?: string;
  mode?: "create" | "edit";
}

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;
const filePreviewUrl = (fileId: string) => `${deriveFileApiBase()}/file-storage/preview/${fileId}`;

export const ReceivingForm: React.FC<ReceivingFormProps> = ({
  form,
  initialValues = {},
  isEdit = false,
  onSaveDraft,
  onSubmit,
  purchaseOrderId,
  mode = "create",
}) => {
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<ReceivingItem[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<ReceivingStatus>(ReceivingStatus.DRAFT);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const previousInitialValuesRef = useRef<any>(null);

  // Get current user identity
  const { data: userIdentity } = useGetIdentity();

  // Fetch purchase orders for selection
  const { data: purchaseOrdersData, isLoading: poLoading } = useList<any>({
    resource: "purchaseOrders",
    filters: purchaseOrderId ? [{ field: "id", operator: "eq", value: purchaseOrderId }] : [],
    meta: { gqlQuery: PURCHASE_ORDER_QUERY },
  });

  const purchaseOrders = Array.isArray(purchaseOrdersData?.data)
    ? purchaseOrdersData?.data
    : (purchaseOrdersData?.data as any)?.purchaseOrders?.nodes || [];

  // Fetch vendors for selection
  const vendorFilters = [];

  // Always include the selected vendor if in edit mode and we have vendor info
  if (isEdit && initialValues?.vendorInfo?.vendorId) {
    vendorFilters.push({
      field: "id",
      operator: "eq" as const,
      value: initialValues.vendorInfo.vendorId,
    });
  }

  const { data: vendorsData, isLoading: vendorsLoading } = useList<any>({
    resource: "vendors",
    filters: vendorFilters,
    meta: { gqlQuery: VENDOR_LIST_QUERY },
  });

  const vendors = Array.isArray(vendorsData?.data)
    ? vendorsData?.data
    : (vendorsData?.data as any)?.vendors?.nodes || [];

  // Fetch purchase templates for selection
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates",
    filters: [
      { field: "isActive", operator: "eq", value: true }
    ],
    meta: { gqlQuery: REQUEST_TEMPLATE_LIST_QUERY },
  });

  const templates = Array.isArray(templatesData?.data)
    ? templatesData?.data
    : (templatesData?.data as any)?.purchaseTemplates?.nodes || [];

  // Separate templates by type
  const receivingTemplates = templates.filter((template: any) => template.templateKind === 'RECEIPT');
  const inspectionTemplates = templates.filter((template: any) => template.templateKind === 'INSPECTION');

  const getMimeTypeIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return <FilePdfOutlined />;
    } else if (mimeType.includes("image")) {
      return <FileImageOutlined />;
    } else if (mimeType.includes("text")) {
      return <FileTextOutlined />;
    }
    return <FileOutlined />;
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Initialize form with values
  useEffect(() => {
    // Check if initialValues have actually changed
    const initialValuesChanged = JSON.stringify(initialValues) !== JSON.stringify(previousInitialValuesRef.current);

    if (!isFormInitialized || (initialValues && initialValuesChanged)) {
      if (initialValues) {
        const formValues = {
          ...initialValues,
          deliveryDate: initialValues?.deliveryDate ? dayjs(initialValues.deliveryDate) : dayjs(),
          expectedDeliveryDate: initialValues?.expectedDeliveryDate ? dayjs(initialValues.expectedDeliveryDate) : undefined,
          inspectionDate: initialValues?.inspectionDate ? dayjs(initialValues.inspectionDate) : dayjs(),
          acceptedDate: initialValues?.acceptedDate ? dayjs(initialValues.acceptedDate) : dayjs(),
          receivedBy: initialValues?.receivedBy || (userIdentity as any)?.name || (userIdentity as any)?.email || "",
        };

        form.setFieldsValue(formValues);
        setCurrentStatus(initialValues.status || ReceivingStatus.DRAFT);

        // Ensure inspection fields are initialized
        form.setFieldsValue({
          acceptedBy: initialValues?.acceptedBy || '',
          inspectionNotes: initialValues?.inspectionNotes || '',
        });

        if (initialValues.items) {
          setItems(initialValues.items);
        }

        if (initialValues.attachments) {
          setAttachments(initialValues.attachments);
        }

        // Set template selections
        if (initialValues.receivingTemplateId) {
          form.setFieldsValue({ receivingTemplateId: initialValues.receivingTemplateId });
        }

        if (initialValues.acceptanceTemplateId) {
          form.setFieldsValue({ acceptanceTemplateId: initialValues.acceptanceTemplateId });
        }
      } else {
        // Set defaults for new receiving
        form.setFieldsValue({
          deliveryDate: dayjs(),
          inspectionDate: dayjs(),
          acceptedDate: dayjs(),
          receivedBy: (userIdentity as any)?.name || (userIdentity as any)?.email || "",
          acceptedBy: '',
          inspectionNotes: '',
        });
        setCurrentStatus(ReceivingStatus.DRAFT);

        // Set default templates for new receiving
        if (!isEdit) {
          // Set first RECEIPT template as default for receiving template
          if (receivingTemplates.length > 0) {
            const defaultReceivingTemplate = receivingTemplates[0];
            form.setFieldsValue({ receivingTemplateId: defaultReceivingTemplate.id });
          }

          // Set first INSPECTION template as default for acceptance template
          if (inspectionTemplates.length > 0) {
            const defaultAcceptanceTemplate = inspectionTemplates[0];
            form.setFieldsValue({ acceptanceTemplateId: defaultAcceptanceTemplate.id });
          }
        }
      }

      if (!isFormInitialized) {
        setIsFormInitialized(true);
      }

      // Update the ref with current initialValues
      previousInitialValuesRef.current = initialValues;
    }
  }, [initialValues, form, userIdentity, templates, receivingTemplates, inspectionTemplates, isFormInitialized, isEdit]);

  // Auto-populate from selected purchase order
  useEffect(() => {
    if (selectedPurchaseOrder) {
      const po = selectedPurchaseOrder;

      // Map PO items to receiving items
      const receivingItems: ReceivingItem[] = po.items?.map((item: any) => ({
        id: `tmp-${Date.now()}-${Math.random()}`,
        name: item.name,
        description: item.description,
        orderedQty: item.quantity || 0,
        receivedQty: 0,
        acceptedQty: 0,
        rejectedQty: 0,
        unit: item.unit,
        unitPrice: item.unitPrice || 0,
        sourceOrderItemId: item.id,
        tags: item.tags || [],
      })) || [];

      setItems(receivingItems);

      // Set vendor info
      if (po.vendor) {
        const vendorInfo: VendorInfo = {
          vendorId: po.vendor.vendorId,
          companyName: po.vendor.companyName,
          contactPerson: po.vendor.contactPerson,
          email: po.vendor.email,
          phone: po.vendor.phone,
          address: po.vendor.address,
          tin: po.vendor.tin,
        };
        form.setFieldsValue({ vendorInfo });
      }

      // Set PO reference
      form.setFieldsValue({
        poId: po.id,
        poNo: po.orderNo,
      });
    }
  }, [selectedPurchaseOrder, form]);

  // Handle vendor selection for edit mode
  useEffect(() => {
    if (isEdit && initialValues?.vendorInfo?.vendorId && vendors.length > 0) {
      const vendorExists = vendors.some((v: any) => v.id === initialValues.vendorInfo!.vendorId);
      if (vendorExists) {
        form.setFieldsValue({
          vendor: initialValues.vendorInfo!.vendorId,
        });
      }
    }
  }, [isEdit, initialValues?.vendorInfo?.vendorId, vendors, form]);

  const handlePurchaseOrderChange = (poId: string) => {
    const po = purchaseOrders.find((order: any) => order.id === poId);
    if (po) {
      setSelectedPurchaseOrder(po);
    }
  };

  // Calculate totals
  const totalAcceptedValue = useMemo(
    () => items.reduce((sum, item) => sum + ((Number(item.acceptedQty) || 0) * (Number(item.unitPrice) || 0)), 0),
    [items],
  );

  // Add or update item
  const saveItem = async () => {
    try {
      const values = await itemForm.validateFields();

      // Find the corresponding PO item if sourceOrderItemId is provided
      const poItem = selectedPurchaseOrder?.items?.find((item: any) => item.id === values.sourceOrderItemId);

      const newItem: ReceivingItem = {
        id: values.id || `tmp-${Date.now()}`,
        name: values.name,
        description: values.description,
        orderedQty: poItem ? poItem.quantity : values.orderedQty || 0,
        receivedQty: values.receivedQty || 0,
        acceptedQty: values.acceptedQty || 0,
        rejectedQty: values.rejectedQty || 0,
        unit: values.unit || poItem?.unit,
        unitPrice: values.unitPrice || poItem?.unitPrice || 0,
        remarks: values.remarks,
        sourceOrderItemId: values.sourceOrderItemId,
        tags: values.tags || [],
      };

      if (editingIndex !== null && editingIndex >= 0) {
        const copy = [...items];
        copy[editingIndex] = { ...copy[editingIndex], ...newItem };
        setItems(copy);
      } else {
        setItems((prev) => [...prev, newItem]);
      }

      setIsItemModalVisible(false);
      itemForm.resetFields();
      setEditingIndex(null);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const editItem = (index: number) => {
    const item = items[index];
    itemForm.setFieldsValue({
      ...item,
    });
    setEditingIndex(index);
    setIsItemModalVisible(true);
  };

  const removeItem = (index: number) => {
    const copy = [...items];
    copy.splice(index, 1);
    setItems(copy);
  };

  // Columns for Receiving Items Table
  const columns: any[] = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: 12, color: "#666" }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Ordered Qty",
      dataIndex: "orderedQty",
      key: "orderedQty",
      width: 120,
      render: (qty: number, record: any) => (
        <div>
          <div>{qty?.toLocaleString() || 0}</div>
          {record.unit && <div style={{ fontSize: 12, color: "#999" }}>{record.unit}</div>}
        </div>
      ),
    },
    {
      title: "Received Qty",
      dataIndex: "receivedQty",
      key: "receivedQty",
      width: 140,
      render: (qty: number, record: any, index: number) => (
        <InputNumber
          min={0}
          max={record.orderedQty}
          step={0.01}
          value={qty}
          readOnly={currentStatus === ReceivingStatus.RECEIVED}
          onChange={(value) => {
            const newItems = [...items];
            const receivedQty = value || 0;
            const currentAccepted = record.acceptedQty || 0;
            const currentRejected = record.rejectedQty || 0;

            // If current accepted + rejected > new received, adjust proportionally
            const currentTotal = currentAccepted + currentRejected;
            let newAccepted = currentAccepted;
            let newRejected = currentRejected;

            if (currentTotal > 0 && currentTotal !== receivedQty) {
              // Maintain the ratio between accepted and rejected
              const acceptedRatio = currentAccepted / currentTotal;
              const rejectedRatio = currentRejected / currentTotal;

              newAccepted = Math.max(0, Math.round(receivedQty * acceptedRatio * 100) / 100);
              newRejected = Math.max(0, receivedQty - newAccepted);
            } else if (receivedQty === 0) {
              // If received is 0, set both to 0
              newAccepted = 0;
              newRejected = 0;
            } else {
              // If no previous values, default to all accepted
              newAccepted = receivedQty;
              newRejected = 0;
            }

            newItems[index] = {
              ...newItems[index],
              receivedQty,
              acceptedQty: newAccepted,
              rejectedQty: newRejected
            };
            setItems(newItems);
          }}
          style={{ width: "100%" }}
          placeholder="0"
        />
      ),
    },
    ...(currentStatus !== ReceivingStatus.DRAFT ? [
      {
        title: "Accepted Qty",
        dataIndex: "acceptedQty",
        key: "acceptedQty",
        width: 140,
        render: (qty: number, record: any, index: number) => (
          <InputNumber
            min={0}
            max={record.receivedQty}
            step={0.01}
            value={qty}
            onChange={(value) => {
              const newItems = [...items];
              const acceptedQty = value || 0;
              const rejectedQty = Math.max(0, (record.receivedQty || 0) - acceptedQty);
              newItems[index] = {
                ...newItems[index],
                acceptedQty,
                rejectedQty
              };
              setItems(newItems);
            }}
            style={{ width: "100%" }}
            placeholder="0"
          />
        ),
      },
      {
        title: "Rejected Qty",
        dataIndex: "rejectedQty",
        key: "rejectedQty",
        width: 140,
        render: (qty: number, record: any, index: number) => (
          <InputNumber
            min={0}
            max={record.receivedQty}
            step={0.01}
            value={qty}
            onChange={(value) => {
              const newItems = [...items];
              const rejectedQty = value || 0;
              const acceptedQty = Math.max(0, (record.receivedQty || 0) - rejectedQty);
              newItems[index] = {
                ...newItems[index],
                acceptedQty,
                rejectedQty
              };
              setItems(newItems);
            }}
            style={{ width: "100%" }}
            placeholder="0"
          />
        ),
      }
    ] : []),
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 120,
      render: (price: number) => `ETB ${Number(price || 0).toLocaleString()}`,
    },
    {
      title: "Total Value",
      dataIndex: "totalValue",
      key: "totalValue",
      width: 120,
      render: (value: number, record: any) =>
        `ETB ${Number((Number(record.acceptedQty) || 0) * (Number(record.unitPrice) || 0)).toLocaleString()}`,
    },
    ...(currentStatus !== ReceivingStatus.DRAFT ? [
      {
        title: "Remarks / Reason",
        dataIndex: "remarks",
        key: "remarks",
        ellipsis: true,
        render: (remarks: string, record: any, index: number) => (
          <Input
            placeholder="Add remarks..."
            value={remarks}
            onChange={(e) => {
              const newItems = [...items];
              newItems[index] = { ...newItems[index], remarks: e.target.value };
              setItems(newItems);
            }}
          />
        ),
      }
    ] : []),
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any, index: number) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => editItem(index)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => removeItem(index)} />
        </Space>
      ),
    },
  ];

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;

    setLoading(true);
    try {
      const values = await form.validateFields();
      console.log({values})
      const payload = {
        ...values,
        items,
        attachments,
        deliveryDate: values.deliveryDate?.toDate(),
        expectedDeliveryDate: values.expectedDeliveryDate?.toDate(),
        inspectionDate: values.inspectionDate?.toDate(),
        acceptedDate: values.acceptedDate?.toDate(),
      };

      // Ensure required fields are present for the backend
      const inputPayload = {
        poId: payload.poId,
        poNo: payload.poNo,
        deliveryDate: payload.deliveryDate,
        expectedDeliveryDate: payload.expectedDeliveryDate,
        vendorInfo: payload.vendorInfo,
        items: payload.items,
        attachments: payload.attachments,
        notes: payload.notes,
        deliveryNotes: payload.deliveryNotes,
        receivedBy: payload.receivedBy,
        tags: payload.tags,
        receivingTemplateId: payload.receivingTemplateId,
        acceptanceTemplateId: payload.acceptanceTemplateId,
       
          inspectionDate: payload.inspectionDate,
          acceptedBy: payload.acceptedBy,
          acceptedDate: payload.acceptedDate,
          inspectionNotes: payload.inspectionNotes,
        
      };

      console.log("Saving draft payload:", inputPayload); // Debug log
      await onSaveDraft(inputPayload);
    } catch (error) {
      console.error("Validation failed:", error);
      message.error("Please check the form for errors");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!onSubmit) return;

    setLoading(true);
    try {
      const values = await form.validateFields();

      // Validate that all items have been processed
      const unprocessedItems = items.filter(item => {
        const receivedQty = item.receivedQty || 0;
        const acceptedQty = item.acceptedQty || 0;
        const rejectedQty = item.rejectedQty || 0;
        return receivedQty > 0 && Math.abs((acceptedQty + rejectedQty) - receivedQty) > 0.01;
      });

      if (unprocessedItems.length > 0) {
        message.warning(
          `${unprocessedItems.length} item(s) need quantity validation. Please ensure accepted + rejected = received for all items.`
        );
        return;
      }

      // Validate inspection fields if status is RECEIVED or DRAFT_ACCEPTANCE
      if (currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE) {
        if (!values.inspectionDate) {
          message.error("Inspection Date is required");
          return;
        }
        if (!values.acceptedBy) {
          message.error("Accepted By is required");
          return;
        }
        if (!values.acceptedDate) {
          message.error("Accepted Date is required");
          return;
        }
        if (!values.inspectionNotes) {
          message.error("Inspection Notes is required");
          return;
        }
      }

      const payload = {
        ...values,
        items,
        attachments,
        deliveryDate: values.deliveryDate?.toDate(),
        expectedDeliveryDate: values.expectedDeliveryDate?.toDate(),
        inspectionDate: values.inspectionDate?.toDate(),
        acceptedDate: values.acceptedDate?.toDate(),
      };

      // Ensure required fields are present for the backend
      const inputPayload = {
        poId: payload.poId,
        poNo: payload.poNo,
        deliveryDate: payload.deliveryDate,
        expectedDeliveryDate: payload.expectedDeliveryDate,
        vendorInfo: payload.vendorInfo,
        items: payload.items,
        attachments: payload.attachments,
        notes: payload.notes,
        deliveryNotes: payload.deliveryNotes,
        receivedBy: payload.receivedBy,
        tags: payload.tags,
        receivingTemplateId: payload.receivingTemplateId,
        acceptanceTemplateId: payload.acceptanceTemplateId,
        // Include inspection fields if status is RECEIVED or DRAFT_ACCEPTANCE
        ...((currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE) && {
          inspectionDate: payload.inspectionDate,
          acceptedBy: payload.acceptedBy,
          acceptedDate: payload.acceptedDate,
          inspectionNotes: payload.inspectionNotes,
        }),
      };

      console.log("Submitting payload:", inputPayload); // Debug log
      await onSubmit(inputPayload);
    } catch (error) {
      console.error("Validation failed:", error);
      message.error("Please check the form for errors");
    } finally {
      setLoading(false);
    }
  };

  // File upload configuration
  const uploadProps = {
    name: "file",
    multiple: true,
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });

        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }

        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }

        const attachment = {
          fileId: fileData.fileId,
          originalName: fileData.originalName,
          mimeType: fileData.mimeType,
          size: fileData.size,
          category: "GENERAL",
        };

        const newAttachments = [...attachments, attachment];
        setAttachments(newAttachments);
        form.setFieldValue("attachments", newAttachments);

        onSuccess?.(json, file);
        message.success(`${file.name} uploaded successfully`);
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
  };

  const handleRemoveAttachment = (fileIdToRemove: string) => {
    const newAttachments = attachments.filter(att => att.fileId !== fileIdToRemove);
    setAttachments(newAttachments);
    form.setFieldValue("attachments", newAttachments);
    message.success("Attachment removed");
  };

  return (
    <div>
      <Form form={form} layout="vertical" initialValues={{ status: ReceivingStatus.DRAFT }}>
        <Card title="Receiving Information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="poId"
                label="Purchase Order"
                rules={[{ required: true, message: "Please select a purchase order" }]}
              >
                <Select
                  placeholder="Select purchase order"
                  loading={poLoading}
                  onChange={handlePurchaseOrderChange}
                  disabled={!!purchaseOrderId || currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} // Disable if PO ID is provided via props or status is RECEIVED or DRAFT_ACCEPTANCE
                >
                  {purchaseOrders.map((po: any) => (
                    <Select.Option key={po.id} value={po.id}>
                      {po.orderNo} - {po.vendor?.companyName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="poNo" label="PO Number">
                <Input disabled placeholder="Auto-filled from selected PO" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="deliveryDate"
                label="Delivery Date"
                rules={[{ required: true, message: "Please select delivery date" }]}
              >
                <DatePicker style={{ width: "100%" }} disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="expectedDeliveryDate" label="Expected Delivery Date">
                <DatePicker style={{ width: "100%" }} disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="receivingNo" label="Receiving Number">
                <Input placeholder="Auto-generated if empty" disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="receivedBy"
                label="Received By"
                rules={[{ required: true, message: "Please enter who received the items" }]}
              >
                <Input
                  placeholder="Name of person who received the items"
                  disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE}
                  defaultValue={(userIdentity as any)?.name || (userIdentity as any)?.email || ""}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="vendor" label="Vendor">
                <Select
                  showSearch
                  placeholder="Select or search vendor"
                  loading={vendorsLoading}
                  onSearch={(value) => {
                    // You can implement vendor search here if needed
                  }}
                  filterOption={false}
                  notFoundContent={vendorsLoading ? "Loading..." : "No vendors found"}
                  disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE}
                >
                  {vendors.map((v: any) => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.companyName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="deliveryNotes" label="Delivery Notes">
                <Input.TextArea rows={3} placeholder="Notes about the delivery" disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="receivingTemplateId"
                label="Receiving Template"
              >
               <Select
                 placeholder="Select receiving template"
                 loading={templatesLoading}
                 disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE}
               >
                  {receivingTemplates.map((template: any) => (
                    <Select.Option key={template.id} value={template.id}>
                      {template.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="notes" label="Internal Notes">
                <Input.TextArea rows={2} placeholder="Internal notes and comments" disabled={currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Inspection Information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="inspectionDate"
                label="Inspection Date"
                rules={[
                  { required: currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE, message: "Please select inspection date" }
                ]}
              >
                <DatePicker style={{ width: "100%" }} disabled={currentStatus !== ReceivingStatus.RECEIVED && currentStatus !== ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="acceptedBy"
                label="Accepted By"
                rules={[
                  { required: currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE, message: "Please enter who accepted the items" }
                ]}
              >
                <Input placeholder="Name of person who accepted the items" disabled={currentStatus !== ReceivingStatus.RECEIVED && currentStatus !== ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="acceptedDate"
                label="Accepted Date"
                rules={[
                  { required: currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE, message: "Please select accepted date" }
                ]}
              >
                <DatePicker style={{ width: "100%" }} disabled={currentStatus !== ReceivingStatus.RECEIVED && currentStatus !== ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="acceptanceTemplateId"
                label="Acceptance Template"
              >
                <Select
                  placeholder="Select acceptance template"
                  loading={templatesLoading}
                  disabled={currentStatus !== ReceivingStatus.RECEIVED && currentStatus !== ReceivingStatus.DRAFT_ACCEPTANCE}
                >
                  {inspectionTemplates.map((template: any) => (
                    <Select.Option key={template.id} value={template.id}>
                      {template.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="inspectionNotes"
                label="Inspection Notes"
                rules={[
                  { required: currentStatus === ReceivingStatus.RECEIVED || currentStatus === ReceivingStatus.DRAFT_ACCEPTANCE, message: "Please enter inspection notes" }
                ]}
              >
                <Input.TextArea rows={3} placeholder="Notes from the inspection process" disabled={currentStatus !== ReceivingStatus.RECEIVED && currentStatus !== ReceivingStatus.DRAFT_ACCEPTANCE} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={
            <Space>
              <ShoppingCartOutlined />
              Receiving Items
            </Space>
          }
          style={{ marginBottom: 24 }}
          extra={
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                itemForm.resetFields();
                setEditingIndex(null);
                setIsItemModalVisible(true);
              }}
            />
          }
        >
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(record) => record.id || `${record.name}-${record.orderedQty}`}
            pagination={false}
            scroll={{ x: 1000 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Totals</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>ETB {totalAcceptedValue.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={3}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>


        <Card title="Attachments" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload Files</Button>
            </Upload>
          </div>

          <Space wrap>
            {attachments.map((att: any) => (
              <div key={att.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                <Space>
                  {getMimeTypeIcon(att.mimeType)}
                  <a href={filePreviewUrl(att.fileId)} target="_blank" rel="noreferrer">
                    {att.originalName} ({formatBytes(att.size)})
                  </a>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveAttachment(att.fileId)}
                  >
                    Remove
                  </Button>
                </Space>
              </div>
            ))}
          </Space>

          {attachments.length === 0 && (
            <div style={{ color: "#999", fontStyle: "italic" }}>No attachments uploaded</div>
          )}
        </Card>

        <Card style={{ textAlign: "right", border: "none" }}>
          <Space>
            <Button
              type="default"
              onClick={handleSaveDraft}
              icon={<SaveOutlined />}
              loading={loading}
              disabled={!onSaveDraft}
            >
              Save Draft
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              icon={<SendOutlined />}
              loading={loading}
              disabled={!onSubmit}
            >
              Submit Receiving
            </Button>
          </Space>
        </Card>
      </Form>

      <Modal
        title={editingIndex !== null ? "Edit Receiving Item" : "Add Receiving Item"}
        open={isItemModalVisible}
        onOk={saveItem}
        onCancel={() => {
          setIsItemModalVisible(false);
          itemForm.resetFields();
          setEditingIndex(null);
        }}
        okText={editingIndex !== null ? "Update Item" : "Add Item"}
        width={800}
        destroyOnClose
      >
        <Form form={itemForm} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="sourceOrderItemId"
                label="Link to Purchase Order Item (Optional)"
              >
                <Select
                  placeholder="Select PO item to auto-fill details"
                  allowClear
                  onChange={(value) => {
                    const poItem = selectedPurchaseOrder?.items?.find((item: any) => item.id === value);
                    if (poItem) {
                      itemForm.setFieldsValue({
                        name: poItem.name,
                        description: poItem.description,
                        unit: poItem.unit,
                        unitPrice: poItem.unitPrice,
                        orderedQty: poItem.quantity,
                      });
                    }
                  }}
                >
                  {selectedPurchaseOrder?.items?.map((item: any) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name} - Qty: {item.quantity} {item.unit} - ETB {item.unitPrice?.toLocaleString()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="name"
                label="Item Name"
                rules={[{ required: true, message: "Please enter item name" }]}
              >
                <Input placeholder="Item name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="unit" label="Unit">
                <Input placeholder="e.g., Pcs, Kg, Box" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} placeholder="Item description" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="orderedQty" label="Ordered Quantity">
                <Input disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="unitPrice" label="Unit Price">
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="tags" label="Tags">
                <Select mode="tags" placeholder="Add tags" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea rows={2} placeholder="Additional remarks" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceivingForm;